import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useState, type ReactNode } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { ClipboardPlusIcon, MilkIcon, WheatIcon } from "lucide-react";
import type { HomeSection } from "@brewbase/lib";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import NewPurchasedItem from "@/features/inventory/NewPurchasedItem";
import { PlanFermentationDialog } from "@/features/fermentation-journal/components/planning/plan-fermentation-dialog";
import { NewPlannedSessionDialog } from "@/features/harvest-journal/components/new-planned-session-dialog";
import { OpenCollectionBanner } from "@/features/tank-board/open-collection-banner";
import { TankTiles } from "@/features/tank-board/tank-tiles";
import { BrothTile } from "@/features/tank-board/broth-tile";
import { Bento } from "@/features/home/bento";
import { HomeHero } from "@/features/home/home-hero";
import { HomeSectionsPopover } from "@/features/home/home-sections-popover";
import { useHomeSections } from "@/features/home/use-home-sections";
import { AlertLine } from "@/features/home/alert-line";
import { HarvestsTile } from "@/features/home/harvests-tile";
import { AnalyticsQueue, QUEUE_CODES } from "@/features/home/analytics-queue";

export const Route = createFileRoute("/")({
  component: HomePage,
  head: () => ({ meta: [{ title: "BrewBase" }] }),
});

const REFRESH = { refetchInterval: 60_000, placeholderData: keepPreviousData };

/**
 * The landing page: the hero, then one sheet of tiles, each answering one
 * question: the tanks, the week's harvests and their batches, the broth
 * coming out of the tanks, and the analytics queues with the viewer's own
 * requests. Every tile
 * reads from data that is reliably recorded. Each operator picks which tiles
 * they see; the layout is fixed, and their own runs and harvest sessions
 * lead their tiles.
 */
function HomePage() {
  const { operator, sections, save } = useHomeSections();
  const shown = new Set<HomeSection>(
    sections.filter((s) => s.shown).map((s) => s.id),
  );
  const needs = (id: HomeSection) => shown.has(id);
  const [showAll, setShowAll] = useState(false);
  const [isNewItemOpen, setIsNewItemOpen] = useState(false);
  const [isPlanOpen, setIsPlanOpen] = useState(false);
  const now = new Date();

  const { data: board } = trpc.fermentations.board.overview.useQuery(
    undefined,
    { ...REFRESH, enabled: needs("tanks") },
  );
  const { data: broth } = trpc.harvest.brothHandover.overview.useQuery(
    { all: showAll },
    { ...REFRESH, enabled: needs("broth") },
  );
  const { data: harvests } = trpc.home.harvests.useQuery(undefined, {
    ...REFRESH,
    enabled: needs("harvests"),
  });
  const { data: batches } = trpc.home.batches.useQuery(undefined, {
    ...REFRESH,
    enabled: needs("harvests"),
  });
  const { data: queues } = trpc.home.analyticsQueues.useQuery(
    { codes: QUEUE_CODES },
    { ...REFRESH, enabled: needs("analytics") },
  );
  const { data: stats } = trpc.home.stats.useQuery(undefined, {
    refetchInterval: 10 * 60_000,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
  const { data: myRequests } = trpc.home.myRequests.useQuery(undefined, {
    ...REFRESH,
    enabled: needs("analytics"),
  });

  const render: Record<HomeSection, () => ReactNode> = {
    tanks: () => (
      <TankTiles
        rows={board?.rows ?? []}
        columns={board?.columns ?? []}
        operatorId={operator?.id}
        now={now}
        loading={!board}
      />
    ),
    broth: () => (
      <BrothTile
        tanks={broth?.activeTanks ?? []}
        bottles={broth?.bottles ?? []}
        showAll={showAll}
        onShowAllChange={setShowAll}
        loading={!broth}
      />
    ),
    harvests: () => (
      <HarvestsTile
        worked={harvests ?? []}
        batches={batches}
        operatorId={operator?.id}
        loading={!harvests}
      />
    ),
    analytics: () => (
      <AnalyticsQueue queues={queues} mine={myRequests} now={now} />
    ),
  };

  return (
    <div className="flex flex-col pb-12">
      <HomeHero
        stats={stats}
        firstName={operator?.first_name}
        actions={
          <HomeSectionsPopover
            sections={sections}
            onChange={(next) => void save(next)}
            onReset={() => void save(null)}
          />
        }
      />
      {/* Clear of the hero's edge; the mark hangs into the right of it. */}
      <div className="container mx-auto flex flex-col gap-6 pt-10">
        <div className="flex min-h-8 flex-wrap items-center gap-1 pr-[200px]">
          <Button
            size="sm"
            variant="ghost"
            className="text-olive-11"
            onClick={() => setIsPlanOpen(true)}
          >
            <MilkIcon />
            Plan fermentation
          </Button>
          <PlanFermentationDialog open={isPlanOpen} onOpenChange={setIsPlanOpen} />
          <NewPlannedSessionDialog>
            <Button size="sm" variant="ghost" className="text-olive-11">
              <WheatIcon />
              Plan harvest session
            </Button>
          </NewPlannedSessionDialog>
          <Dialog open={isNewItemOpen} onOpenChange={setIsNewItemOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="ghost" className="text-olive-11">
                <ClipboardPlusIcon />
                New purchased item
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[720px]">
              <NewPurchasedItem
                asModal
                onComplete={() => setIsNewItemOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
        <AlertLine />
        <OpenCollectionBanner flags={broth?.flags.openOnEndedRun ?? []} />
        {shown.size === 0 ? (
          <p className="text-sm text-olive-9">
            All sections are hidden. Turn one back on from Sections.
          </p>
        ) : (
          <Bento>
            {sections
              .filter((s) => s.shown)
              .map((s) => (
                <Fragment key={s.id}>{render[s.id]()}</Fragment>
              ))}
          </Bento>
        )}
      </div>
    </div>
  );
}

